import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import {
    AisRestService,
    CaseResource,
    CaseResourceIdentifier,
    ConditionDisclosureResource,
    DisclosureSource,
    InterviewDisclosureIdentifier,
    QuestionAnswerType,
    ReflexiveAnswerType,
    ReflexiveQuestionResource
} from '@Components/mras/core';
import { NotificationService, NotificationSeverity, ObservableService } from '@Services';
import { BooleanInputComponent } from '../../controls/boolean-input.component';
import { DateInputComponent } from '../../controls/date-input.component';
import { ShortDateInputComponent } from '../../controls/short-date-input.component';
import { NumericInputComponent } from '../../controls/numeric-input.component';
import { TextInputComponent } from '../../controls/text-input.component';
import { ReflexivePicklistInputComponent } from '../../controls/reflexive-picklist-input.component';
import { SelectInputComponent } from '../../controls/select-input.component';
import { SharedModule } from '@Shared/shared.module';

@Component({
    selector: 'jhi-questions-reflexive',
    templateUrl: './questions-reflexive.component.html',
    styles: [],
    imports: [SharedModule, BooleanInputComponent, DateInputComponent, ShortDateInputComponent,
        NumericInputComponent, TextInputComponent, ReflexivePicklistInputComponent, SelectInputComponent
    ]
})
export class QuestionsReflexiveComponent implements OnInit {
    readonly QuestionAnswerType = QuestionAnswerType;
    readonly DisclosureSource = DisclosureSource;
    readonly ReflexiveQuestionType = ReflexiveAnswerType;

    @Input()
        summaryMode: boolean;
    @Input()
        disclosureSource: DisclosureSource;

    @Input()
        disclosureIdentifier: InterviewDisclosureIdentifier;
    @Input()
        disclosure: ConditionDisclosureResource;
    @Output()
        disclosureChange = new EventEmitter<string>();
    @Output()
        onRemove = new EventEmitter<string>();
    @Output()
        onRemoveTriggeredDisclosure = new EventEmitter<string[]>();
    @Output()
        onUpdate = new EventEmitter<string>();
    @Input()
        showDisclosureName: boolean;
    @Input()
        caseDetails: CaseResource;
    @Input()
        category: string;    
    
    disclosureName: string;
    httpErrorMsg: string;
    private readonly genericRetryMessage = 'Something went wrong. Please try again.';

    constructor(private aisRestService: AisRestService,
        public observableService: ObservableService,
        private notificationService: NotificationService
    ) {}

    private showGenericRetryToast() {
        this.notificationService.showNotification({
            severity: NotificationSeverity.Error,
            message: this.genericRetryMessage
        });
    }

    ngOnInit(): void {
        this.disclosureName = this.disclosure.aliasName;
        const caseIdentifier = new CaseResourceIdentifier();
        caseIdentifier.caseId = this.disclosureIdentifier.caseId;
    }


    answerReflexivePicklistQuestion(currentQuestion: any, answerHasChanged: boolean) {
        this.httpErrorMsg = null;        
        
        const disclosureQuestion = this.disclosure.questions.find(x => x.code === currentQuestion.code);
        let answerVal = undefined;
        
        if (disclosureQuestion.triggeredAliases && disclosureQuestion.triggeredAliases.length > 0) {
            answerVal = disclosureQuestion.triggeredAliases[0];
        }

        if (answerHasChanged) {
            disclosureQuestion.answerValue = null;        
        }
        
        this.submitReflexivePicklistQuestion(answerHasChanged, currentQuestion, answerVal);  
    }    

    answerReflexiveQuestion(currentQuestion: any, answerHasChanged: boolean) {
        this.httpErrorMsg = null;        
        
        const disclosureQuestion = this.disclosure.questions.find(x => x.code === currentQuestion.code);
        const answerVal = disclosureQuestion.answerValue;

        if (answerHasChanged) {
            disclosureQuestion.answerValue = null;        
        }
        
        this.submitReflexiveQuestion(answerHasChanged, currentQuestion, answerVal);  
    }

    private clearFailedReflexiveAnswer(currentQuestion: any) {
        const disclosureQuestion = this.disclosure.questions.find(x => x.code === currentQuestion.code);
        if (disclosureQuestion) {
            if (disclosureQuestion.answerType === 'PICK_LIST') {
                disclosureQuestion.triggeredAliases = null;
            }
            disclosureQuestion.answerValue = null;
        }
    }

    private handleFailedReflexiveAnswer(currentQuestion: any) {
        if (!this.aisRestService.isClearFailedMrasAnswersEnabled()) {
            return;
        }

        this.clearFailedReflexiveAnswer(currentQuestion);
        this.showGenericRetryToast();
    }

    submitReflexivePicklistQuestion(answerHasChanged: boolean, currentQuestion, currentValue) {
        const resource = new ConditionDisclosureResource();

        //resource.aliasName = currentValue;
        resource.questions = this.disclosure.questions.map(e => {
            const question = new ReflexiveQuestionResource();
            if (e.answerType !== 'PICK_LIST') {
                question.answerValue = e.answerValue;
            } else {
                if (e.triggeredAliases === null || e.picklistQuestionMeta.pickListNoneOfTheAboveVisible === false) 
                    {
                        question.answerValue = 'true';
                    }else{
                        question.answerValue = 'false';
                    }
                
                question.triggeredAliases = e.triggeredAliases;
            }
            return question;
        });

        const existingTriggeredAliases = this.disclosure.triggeredCondAliases;
        this.aisRestService.answerReflexiveQuestion(this.disclosureIdentifier, resource).subscribe(
            answerReflexiveQuestionResp => {                
                if (answerReflexiveQuestionResp.errorCode && answerReflexiveQuestionResp.errorCode !== '') {
                    this.handleFailedReflexiveAnswer(currentQuestion);
                    this.httpErrorMsg = answerReflexiveQuestionResp.errorCode;
                    return;
                }

                Object.keys(this.disclosure).forEach(f => delete this.disclosure[f]);
                Object.assign(this.disclosure, answerReflexiveQuestionResp);
                this.disclosureChange.emit(this.disclosure.aliasName);

                if (existingTriggeredAliases?.sort().toString() !== this.disclosure.triggeredCondAliases?.sort().toString()) {
                    const removedDisclosures = [];
                    for (const existingTriggeredAlias of existingTriggeredAliases) {
                        if (this.disclosure.triggeredCondAliases.indexOf(existingTriggeredAlias) < 0) {
                            removedDisclosures.push(existingTriggeredAlias);
                        }
                    }
                    this.onRemoveTriggeredDisclosure.emit(removedDisclosures);
                    resource.questions.forEach(q => {
                        if (q.triggeredAliases && q.triggeredAliases.length > 0) {
                            q.triggeredAliases.forEach(alias => {
                                this.onUpdate.emit(alias);                                
                            });
                        }
                    });
                }

                if (answerHasChanged) {
                    this.disclosure.questions.find(x => x.code === currentQuestion.code).answerValue = currentValue;   
                    this.submitReflexivePicklistQuestion(false, currentQuestion, currentValue);
                }

            },
            errMsg => {
                this.handleFailedReflexiveAnswer(currentQuestion);
                this.httpErrorMsg = errMsg;
            }
        );
    }

    submitReflexiveQuestion(answerHasChanged: boolean, currentQuestion, currentValue) {
        const resource = new ConditionDisclosureResource();
        let question = new ReflexiveQuestionResource();

        let breakLoop = false;

        resource.questions = this.disclosure.questions.map(e => {
            if (e.answerType !== 'PICK_LIST' && !breakLoop) {
                question = new ReflexiveQuestionResource();
                question.answerValue = e.answerValue;
                
                if (e.code === currentQuestion.code) {
                    breakLoop = true;
                }

                return question;
            } else {
                question.triggeredAliases = e.triggeredAliases;

                if (this.disclosure.questions.length === 1) {
                    return question;
                }
            }

            
        });

        resource.questions = resource.questions.filter( x=> x && x !== null && x.answerValue !== undefined);

        const existingTriggeredAliases = this.disclosure.triggeredCondAliases;
        this.aisRestService.answerReflexiveQuestion(this.disclosureIdentifier, resource).subscribe(
            answerReflexiveQuestionResp => {                
                if (answerReflexiveQuestionResp.errorCode && answerReflexiveQuestionResp.errorCode !== '') {
                    this.handleFailedReflexiveAnswer(currentQuestion);
                    this.httpErrorMsg = answerReflexiveQuestionResp.errorCode;
                    return;
                }

                Object.keys(this.disclosure).forEach(f => delete this.disclosure[f]);
                Object.assign(this.disclosure, answerReflexiveQuestionResp);
                this.disclosureChange.emit(this.disclosure.aliasName);

                if (existingTriggeredAliases?.sort().toString() !== this.disclosure.triggeredCondAliases?.sort().toString()) {
                    const removedDisclosures = [];
                    for (const existingTriggeredAlias of existingTriggeredAliases) {
                        if (this.disclosure.triggeredCondAliases.indexOf(existingTriggeredAlias) < 0) {
                            removedDisclosures.push(existingTriggeredAlias);
                        }
                    }
                    this.onRemoveTriggeredDisclosure.emit(removedDisclosures);
                    this.onUpdate.emit(this.disclosure.aliasName);
                }

                if (answerHasChanged) {
                    this.disclosure.questions.find(x => x.code === currentQuestion.code).answerValue = currentValue;   
                    this.submitReflexiveQuestion(false, currentQuestion, currentValue);
                }

                this.observableService.onMrasQuestionUpdated.next(1);
            },
            errMsg => {
                this.handleFailedReflexiveAnswer(currentQuestion);
                this.httpErrorMsg = errMsg;
            }
        );
    }

    isRemovable(): boolean {
        if (
            this.disclosure.removableFromBqCodes == null ||
            this.disclosure.removableFromBqCodes.length == 0 ||
            this.disclosureSource !== DisclosureSource.SEARCH
        ) {
            return false;
        }
        return this.disclosure.removableFromBqCodes.indexOf(this.disclosureIdentifier.baseQuestionCode) > -1;
    }

    onRemoveClick() {
        this.disclosureIdentifier.aliasName = "";
        this.onRemove.emit(this.disclosureName);
    }
}
